<template>

  <v-container fluid class="provider-connection-card" v-if="connection">

    <v-row>
      <!-- Connection Info -->
      <v-col cols="12" xs="12" sm="12" md="5" lg="4" class="container-connection-info">
        <v-row>
          <v-col cols="12" class="d-flex">
            <div class="mb-3 provider-logo-container"><leaf-link-logo /></div>
            <div class="m-dash-text">&mdash;</div>
            <div class="connection-status-text-active" v-if="status === 'ACTIVE'">{{ status }}</div>
            <div style="font-size:1.35em;color:#aa1e1e" v-else>{{ status }}</div>
          </v-col>
        </v-row>

        <!-- Provider name -->
        <v-row no-gutters class="mt-1">
          <v-col cols="12">
            <span class="pr-3" style="color:#9c9c9c;">Provider:</span>
            <span class="field-value">{{ providerNameForDisplay }}</span>
          </v-col>
        </v-row>

        <!-- Account name -->
        <v-row no-gutters class="mt-7 mb-2">
          <v-col cols="12" class="field-label">
            Account
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" class="field-value">
            {{ account.name }}
          </v-col>
        </v-row>

        <!-- Enabled brands -->
        <template v-if="showEnabledBrands">
          <v-row no-gutters class="mt-5 mb-2">
            <v-col cols="12" class="field-label">
              Enabled Brands
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" style="font-size:1.13em">
              <div v-for="brand in enabledBrands" :key="brand.thirdparty_id">
                {{ brand.name }}
              </div>
            </v-col>
          </v-row>
        </template>

        <!-- Connection edit -->
        <v-row no-gutters class="mt-6">
          <v-col cols="12" align="center">
            <v-btn text color="amber" class="white--text" @click="editConnection">
              <v-icon class="mr-2" style="font-size:18px;">mdi-link</v-icon>
              Manage Connection
            </v-btn>
          </v-col>
        </v-row>
      </v-col>

      <!------------------>
      <!-- Shipper Info -->
      <!------------------>
      <v-col cols="12" xs="12" sm="12" md="7" lg="7" class="container-shipper-info">
        <v-row no-gutters class="mt-1 mb-1">
          <v-col cols="12" xs="12" sm="7" md="7" lg="7" class="field-label">
            Connected Shipper
          </v-col>
          <v-col cols="12" xs="12" sm="5" md="5" lg="5" class="d-flex align-center justify-end">
            <v-chip label outlined :class="`${shipper.shipper_status}`">
              {{ shipper.shipper_status }}
            </v-chip>
            <v-tooltip bottom v-if="shipper.shipper_status !== 'APPROVED'">
              <template v-slot:activator="{ on, attrs }">
                <v-icon style="cursor:pointer;font-size:19px;margin-left:8px;" color="grey darken-1" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <div>While unapproved, deliveries cannot be picked-up from this location.</div>
              <div>A valid license with a ship-to address must be submitted and reviewed to be approved.</div>
            </v-tooltip>
          </v-col>
        </v-row>
        <!-- shipper name -->
        <v-row no-gutters class="mt-5">
          <v-col cols="12" style="font-size:1.13rem">
            {{ shipper.name }}
          </v-col>
        </v-row>
        <!-- shipper address -->
        <v-row no-gutters class="mt-4">
          <v-col cols="12" style="color:#c9c9c9;font-size:1.13em">
            <div>{{ multiLineAddress.street_1 }}</div>
            <span v-if="multiLineAddress.street_2">,</span> {{ multiLineAddress.street_2 }}
            <template v-if="multiLineAddress.area">{{ multiLineAddress.area }}</template>
          </v-col>
        </v-row>
        <!-- shipper DBA -->
        <v-row no-gutters class="mt-8">
          <v-col cols="12" style="font-size:1.0rem">
            <span class="field-label mr-2">DBA:</span>
            {{ shipper.dba }}
          </v-col>
        </v-row>
        <!-- shipper main license number -->
        <v-row no-gutters class="mt-5" v-if="mainLicenseNumber">
          <v-col cols="12" style="font-size:1.0rem">
            <span class="field-label mr-2">License:</span>
            {{ mainLicenseNumber }}
            <span class="ml-2" v-if="mainLicenseCategory">({{ mainLicenseCategory }})</span>
          </v-col>
        </v-row>
        <!-- shipper view action -->
        <v-row no-gutters class="mt-10 mb-2">
          <v-col cols="12" align="center">
            <router-link :to="generateShipperInfoPageLink()">
              <v-btn color="primary">
                View Shipper Info
              </v-btn>
            </router-link>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
import LeafLinkLogo from '@/components/app/LeafLinkLogo'

export default {
  name: 'ProviderConnectionCard',

  components: {
    LeafLinkLogo
  },

  props: {
    connection: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      showEnabledBrands: true
    }
  },

  computed: {
    providerAlias () {
      return this.connection.provider_alias
    },
    providerNameForDisplay () {
      return this.$root.providerAliasToName(this.providerAlias)
    },
    settings () {
      return this.connection.settings || {}
    },
    status () {
      return this.connection.status
    },
    // -------- account
    account () {
      return this.connection.account || {}
    },
    // -------- brands
    accountBrands () {
      return this.connection.brands || []
    },
    enabledBrandIds () {
      return this.settings.brands
    },
    enabledBrands () {
      return this.accountBrands.filter(item => this.$root.utils.includes(this.enabledBrandIds, item.thirdparty_id))
    },
    // -------- shipper info
    shipper () {
      return this.connection.shipper || {}
    },
    multiLineAddress () {
      return this.$root.utils.getAddressTextFromData(this.shipper, { multi_lines: true })
    },
    licenseType () {
      return this.shipper.license_for_shipping_address
    },
    mainLicenseNumber () {
      return (this.licenseType)
        ? this.shipper[`${this.licenseType}_number`]
        : null
    },
    mainLicenseCategory () {
      return (this.licenseType)
        ? this.$root.utils.toTitleCase(this.shipper[`${this.licenseType}_category`])
        : null
    }
  },

  methods: {
    // Emit connection edit action
    editConnection () {
      this.$emit('editConnection', this.providerAlias)
    },

    // Generate link data for shipper info page
    generateShipperInfoPageLink () {
      if (this.shipper.id) {
        return {
          name: 'shipper-item',
          params: {
            shipperId: this.shipper.id
          }
        }
      } else {
        return ''
      }
    }
  }
}
</script>

<style scoped>
  .provider-connection-card .container-connection-info {
    background-color: #161616;
    border-top: 1px solid #262626;
    border-left: 1px solid #262626;
    border-bottom: 1px solid #262626;
    border-radius: 4px 0 0 4px;
    padding: 18px 24px;
  }

  .provider-connection-card .provider-logo-container {
    padding-left: 5px;
    padding-right: 5px;
    padding-top: 5px;
    border-radius: 2px;
    background-color: #f2f2f2;
  }

  .provider-connection-card .m-dash-text {
    color: #9a9a9a;
    padding-top: 3px;
    padding-right: 14px;
    padding-left: 16px;
  }

  .provider-connection-card .connection-status-text-active {
    font-size: 1.35em;
    color: #8DBF42;
  }

  .provider-connection-card .connection-status-text-inactive {
    font-size: 1.35em;
    color: #aa1e1e;
  }

  .provider-connection-card .field-label {
    font-style: italic;
    color: #9c9c9c
  }

  .provider-connection-card .field-value {
    font-size: 1.13em;
  }

  .provider-connection-card .container-shipper-info {
    background-color: #161616;
    border-top: 1px solid #262626;
    border-right: 1px solid #262626;
    border-bottom: 1px solid #262626;
    border-left: 1px solid #262626;
    border-radius: 0 4px 4px 0;
    padding: 18px 24px;
  }
</style>
